import enVoice from "../assets/voices/en.json";
import zhCNVoice from "../assets/voices/zh-CN.json";
import zhHKVoice from "../assets/voices/zh-HK.json";

const estimateTime = (text: string, utteranceRate: number = 1) => {
  const WORDS_PER_MINUTE = 150;
  const wordCount = text.split(" ").length;
  const wordsPerSecond = (WORDS_PER_MINUTE * utteranceRate) / 60;
  return (wordCount / wordsPerSecond) * 1000;
};

function isSpeechNotSupported() {
  const speechNotSupported = !('speechSynthesis' in window)
  return speechNotSupported;
}

export const speak = (
  text: string,
  voiceLang: string,
  voiceOn: boolean,
  selectedVoiceName: string,
  onStart: Function,
  onError: Function,
  onFinish?: Function
) => {
  if (isSpeechNotSupported()) return;

  const synth = window.speechSynthesis;

  const voices = synth.getVoices();
  const startTime = performance.now();
  const defaultVoice =
    voices.find((voice) => voice.name === selectedVoiceName) ||
    voices.find((voice) => voice.lang === voiceLang);

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = voiceLang;
  utterance.volume = voiceOn ? 1 : 0;

  if (onFinish) {
    utterance.onend = () => {
      onFinish();
    };
  }
  utterance.onstart = () => {
    onStart();
    console.log("start speak text:", text);
  }
  utterance.onerror = (e) => {
    console.error("error", e);
    // console.error("Text interrupted", e.utterance.text);
    const estimatedDuration = estimateTime(e.utterance.text);
    const endTime = performance.now();
    const elapseTime = endTime - startTime;
    const remainingTime =
      elapseTime !== null ? Math.max(0, estimatedDuration - elapseTime) : 0;

    // console.error("e.elapsedTime", elapseTime);
    // console.error("estimatedDuration", estimatedDuration);
    // console.error("remainingTime before onFinish", remainingTime);
    if (onError) {
      setTimeout(() => {
        onError();
      }, remainingTime);
    }
  };

  if (defaultVoice) utterance.voice = defaultVoice;

  synth.speak(utterance);
  return synth;
};

export const pauseSpeaking = () => {
  if (isSpeechNotSupported()) return;
  window.speechSynthesis.pause();
};

export const resumeSpeaking = () => {
  if (isSpeechNotSupported()) return;
  window.speechSynthesis.resume();
};

export const stopSpeaking = () => {
  if (isSpeechNotSupported()) return;
  window.speechSynthesis.cancel();
};

export const filterAvailableVoiceNames = (
  language: string,
  onVoicesLoaded?: (voices: any[]) => void
) => {

  const synth = window.speechSynthesis;
  let voices = synth.getVoices();

  // voice not yet loaded
  if (voices.length === 0) {
    synth.addEventListener(
      "voiceschanged",
      () => {
        voices = synth.getVoices();
        const filteredVoices = performVoiceFiltering(voices, language);
        if (onVoicesLoaded) {
          onVoicesLoaded(filteredVoices);
        }
      },
      { once: true }
    );
    return [];
  }

  return performVoiceFiltering(voices, language);
};

const performVoiceFiltering = (
  voices: SpeechSynthesisVoice[],
  language: string
) => {
  var jsonData;
  if (language === "zh-HK") {
    jsonData = zhHKVoice;
  } else if (language === "zh-CN") {
    jsonData = zhCNVoice;
  } else {
    jsonData = enVoice;
  }

  const availableVoices: any[] = [];
  jsonData.voices.forEach(function (voice) {
    if (voices.some((apiVoice) => apiVoice.name === voice.name)) {
      availableVoices.push(voice);
    } else {
      if (voice.altNames) {
        voice.altNames.forEach(function (altName) {
          if (voices.some((apiVoice) => apiVoice.name === altName)) {
            voice.name = altName;
            availableVoices.push(voice);
          }
        });
      }
    }
  });

  if (availableVoices.length > 0) {
    return availableVoices.map((v) => {
      return { code: v.name, name: v.name };
    });
  } else {
    return voices
      .filter((voice) => voice.lang === language)
      .map((v) => {
        return { code: v.name, name: v.name };
      });
  }
};
